import { useState, useEffect } from "react";
import { frentistaApi, apiError, onlyDigits } from "@/lib/api";
import { DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { modal } from "@/lib/modal";
import { ArrowRight, AlertTriangle, Users, Copy, Car, Users2 } from "lucide-react";

// Máscaras BR (só display — o submit usa onlyDigits/upper, então o valor salvo fica limpo).
const maskTel = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
const maskCPF = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const maskPlaca = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);

export function NewClientForm({ initialPlaca, onCreated }) {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cpf, setCpf] = useState("");
  const [placa, setPlaca] = useState(maskPlaca(initialPlaca || ""));
  const [codigoInd, setCodigoInd] = useState("");
  const [isAppDriver, setIsAppDriver] = useState(false);
  const [podeMarcarAppDriver, setPodeMarcarAppDriver] = useState(false);
  const [loading, setLoading] = useState(false);
  // Fluxo v2.28: o cadastro SÓ é criado no servidor APÓS o código SMS correto (ou contingência).
  // `aguardandoOtp` = etapa do código; os dados do form ficam no state até a confirmação.
  const [aguardandoOtp, setAguardandoOtp] = useState(null); // null | { otp_enviado: bool }
  const [codigo, setCodigo] = useState("");
  const [showLib, setShowLib] = useState(false); // liberação especial (contingência) no 1º cadastro
  const [senhaLib, setSenhaLib] = useState("");
  // Anti-spam do Reenviar: intervalo mínimo de 10s entre reenvios (contagem no botão)
  const [cooldown, setCooldown] = useState(0);
  // v2.29 — Grupo de consumo: só aparece se o Master liberar o vínculo pelo frentista
  // e houver grupos ativos. Marcar "Vincular a grupo de consumo" abre a lista dos ativos.
  const [gruposAtivos, setGruposAtivos] = useState([]);
  const [vincularGrupo, setVincularGrupo] = useState(false);
  const [grupoId, setGrupoId] = useState("");
  // O grupo é subgrupo da categoria: lista só os grupos da categoria do cadastro.
  const categoriaCadastro = isAppDriver && podeMarcarAppDriver ? "app_driver" : "normal";
  const gruposDaCategoria = gruposAtivos.filter((g) => (g.categoria || "normal") === categoriaCadastro);
  useEffect(() => {
    if (grupoId && !gruposDaCategoria.some((g) => g.id === grupoId)) setGrupoId("");
    if (!gruposDaCategoria.length && vincularGrupo) setVincularGrupo(false);
  }, [categoriaCadastro, gruposAtivos.length]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/master/config-publica`)
      .then((r) => r.json())
      .then((d) => setPodeMarcarAppDriver(!!d.vendedor_pode_marcar_app_driver))
      .catch(() => {});
    frentistaApi.get("/grupos-consumo/ativos")
      .then((r) => setGruposAtivos(r.data.vinculo_frentista_ativo ? (r.data.grupos || []) : []))
      .catch(() => setGruposAtivos([]));
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const buildPayload = () => ({
    nome, telefone: onlyDigits(telefone), cpf: cpf ? onlyDigits(cpf) : null,
    placa: placa.toUpperCase(), codigo_indicacao: codigoInd || null,
    categoria: isAppDriver && podeMarcarAppDriver ? "app_driver" : "normal",
    grupo_consumo_id: vincularGrupo && grupoId ? grupoId : null,
  });

  const submit = async () => {
    if (!nome || !telefone || !placa) return modal.error("Nome, telefone e placa são obrigatórios");
    if (vincularGrupo && !grupoId) return modal.error("Selecione o grupo de consumo (ou desmarque a opção)");
    setLoading(true);
    try {
      // Pré-cadastro: valida os dados e envia o SMS SEM criar o cliente.
      const { data } = await frentistaApi.post("/clientes/validar-cadastro-otp", buildPayload());
      if (!data.otp_requerido) {
        const { data: c } = await frentistaApi.post("/clientes", buildPayload());
        onCreated(c);
        return;
      }
      setCodigo(""); setSenhaLib(""); setShowLib(false);
      setAguardandoOtp({ otp_enviado: !!data.otp_enviado });
      if (data.otp_enviado) setCooldown(10);  // o 1º SMS acabou de sair — segura o Reenviar
    } catch (err) { modal.error(apiError(err)); }
    finally { setLoading(false); }
  };

  // Código correto → o cadastro é CRIADO agora. Código errado → erro e PERMANECE na tela.
  const verificar = async () => {
    setLoading(true);
    try {
      const { data: c } = await frentistaApi.post("/clientes", { ...buildPayload(), codigo_otp: codigo });
      onCreated(c);
    } catch (err) { setCodigo(""); modal.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const reenviar = async () => {
    if (cooldown > 0) return;  // anti-spam: 1 reenvio a cada 10s
    setCooldown(10);
    try {
      const { data } = await frentistaApi.post("/otp/reenviar-cadastro-sms", { telefone: onlyDigits(telefone) });
      setAguardandoOtp({ otp_enviado: !!data.enviado });
      if (data.enviado) modal.success("Código reenviado por SMS para o cliente.");
      else modal.error("Não foi possível enviar o SMS. Verifique o saldo/config de SMS.");
    } catch (err) { modal.error(apiError(err)); }
  };

  // Única saída sem o código: senha de contingência — cria o cadastro e registra auditoria.
  const liberarContingencia = async () => {
    setLoading(true);
    try {
      const { data: c } = await frentistaApi.post("/clientes", { ...buildPayload(), contingencia_senha: senhaLib });
      setShowLib(false); setSenhaLib("");
      onCreated(c);
    } catch (err) { modal.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const inputCls = "mt-1 w-full h-11 rounded-xl bg-white border border-neutral-200 px-3.5 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

  // Etapa de validação do código enviado por SMS (só no 1º cadastro, quando o Master ativa).
  // O cadastro AINDA NÃO EXISTE aqui — só é criado com o código correto (ou contingência).
  // Fechar a janela manualmente = nada é salvo.
  if (aguardandoOtp) {
    return (
      <div className="space-y-3.5" data-testid="new-client-otp">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Validar telefone</DialogTitle>
        </DialogHeader>
        {aguardandoOtp.otp_enviado ? (
          <p className="text-[12px] text-neutral-600">
            Enviamos um código por <b>SMS</b> para o telefone informado. O cadastro <b>só será criado
            após a confirmação do código</b>. Se fechar esta janela, nada será salvo.
          </p>
        ) : (
          <div className="rounded-lg p-2.5 text-[12px] border border-amber-200 bg-amber-50 text-amber-800 flex items-start gap-1.5">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>Não foi possível enviar o SMS agora. Use <b>Reenviar</b> ou a <b>Liberação especial</b> (senha de contingência). O cadastro só será criado após a validação.</span>
          </div>
        )}

        {!showLib ? (
          <>
            <input data-testid="new-otp-input" value={codigo}
                   onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 4))}
                   maxLength={4} inputMode="numeric" autoFocus
                   className="digit-input" style={{ fontSize: 30, letterSpacing: "8px", padding: "12px 16px", textAlign: "center" }} />
            <button data-testid="new-otp-verify" onClick={verificar} disabled={codigo.length !== 4 || loading}
                    className="w-full h-12 btn-primary-blue text-sm font-medium flex items-center justify-center gap-2">
              {loading ? "Validando..." : (<>Validar e cadastrar <ArrowRight size={14} /></>)}
            </button>
            <div className="grid grid-cols-2 gap-1.5">
              <button data-testid="new-otp-resend" onClick={reenviar} disabled={cooldown > 0}
                      className="btn-ghost h-9 text-[11px] font-medium disabled:opacity-45">
                {cooldown > 0 ? `Reenviar SMS (${cooldown}s)` : "Reenviar SMS"}
              </button>
              <button data-testid="new-otp-lib" onClick={() => setShowLib(true)}
                      className="h-9 rounded-full text-[11px] font-medium text-rose-700 hover:bg-rose-50 transition-colors flex items-center justify-center gap-1">
                <AlertTriangle size={11} /> Liberação especial
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-2.5" data-testid="new-otp-lib-form">
            <p className="text-[11px] text-neutral-600">
              Cadastrar sem o código exige a <b>senha de contingência</b>. Este uso fica <b>registrado para auditoria</b> no painel master.
            </p>
            <input data-testid="new-otp-lib-senha" type="password" value={senhaLib}
                   onChange={(e) => setSenhaLib(e.target.value)} autoFocus
                   placeholder="Senha de contingência"
                   className="w-full h-12 rounded-xl bg-white border border-neutral-200 px-3.5 text-base outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15 transition" />
            <div className="grid grid-cols-2 gap-1.5">
              <button data-testid="new-otp-lib-cancel" onClick={() => { setShowLib(false); setSenhaLib(""); }}
                      className="btn-ghost h-11 text-[12px] font-medium">
                Voltar
              </button>
              <button data-testid="new-otp-lib-ok" onClick={liberarContingencia} disabled={!senhaLib || loading}
                      className="btn-accent h-11 text-[12px] font-medium flex items-center justify-center gap-1.5">
                {loading ? "Cadastrando..." : (<>Cadastrar com contingência <ArrowRight size={13} /></>)}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="new-client-form">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-2xl">Primeiro Acesso</DialogTitle>
      </DialogHeader>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Nome *</label>
        <input data-testid="new-nome" value={nome} onChange={(e) => setNome(e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Telefone WhatsApp *</label>
        <input data-testid="new-telefone" value={telefone} onChange={(e) => setTelefone(maskTel(e.target.value))}
               inputMode="numeric" maxLength={16} placeholder="(51) 99999-9999" className={inputCls} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">CPF (opcional)</label>
        <input data-testid="new-cpf" value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))}
               inputMode="numeric" maxLength={14} placeholder="000.000.000-00" className={inputCls} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Placa *</label>
        <input data-testid="new-placa" value={placa} onChange={(e) => setPlaca(maskPlaca(e.target.value))}
               maxLength={7} placeholder="ABC1D23" className={`${inputCls} uppercase`} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Código de Indicação (opcional)</label>
        <input data-testid="new-codigo-ind" value={codigoInd} onChange={(e) => setCodigoInd(e.target.value.toUpperCase())} className={`${inputCls} uppercase`} />
      </div>
      {podeMarcarAppDriver && (
        <label className="flex items-center gap-2 cursor-pointer rounded-xl bg-amber-50 border border-amber-200 p-3"
               data-testid="new-app-driver-toggle">
          <input type="checkbox" checked={isAppDriver}
                 onChange={(e) => setIsAppDriver(e.target.checked)}
                 className="w-5 h-5 accent-amber-700" />
          <span className="text-[12px] text-amber-900">
            <Car size={13} className="inline-block -mt-0.5" /> <b>Motorista de App</b> — usa preços e descontos da categoria especial
          </span>
        </label>
      )}
      {gruposDaCategoria.length > 0 && (
        <div className="rounded-xl bg-blue-50/70 border border-blue-200 p-3 space-y-2" data-testid="new-grupo-box">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="new-grupo-toggle">
            <input type="checkbox" checked={vincularGrupo}
                   onChange={(e) => { setVincularGrupo(e.target.checked); if (!e.target.checked) setGrupoId(""); }}
                   className="w-5 h-5 accent-[color:var(--brand-blue)]" />
            <span className="text-[12px] text-[color:var(--brand-blue)]">
              <Users2 size={13} className="inline-block -mt-0.5" /> <b>Vincular a grupo de consumo</b>
            </span>
          </label>
          {vincularGrupo && (
            <select data-testid="new-grupo-select" value={grupoId} onChange={(e) => setGrupoId(e.target.value)}
                    className={inputCls.replace("mt-1 ", "")}>
              <option value="">Selecione o grupo...</option>
              {gruposDaCategoria.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nome}{g.regra_desconto === "somente_sistema" || !g.tipo_desconto ? ""
                    : ` — ${g.tipo_desconto === "percentual"
                      ? `${Number(g.valor).toLocaleString("pt-BR")}% de desconto`
                      : `${Number(g.valor).toLocaleString("pt-BR")}¢ por litro`}`}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      <DialogFooter>
        <button data-testid="new-submit" onClick={submit} disabled={loading}
                className="w-full h-12 btn-primary-blue text-sm font-medium flex items-center justify-center gap-2">
          {loading ? "Cadastrando..." : (<>Cadastrar <ArrowRight size={14} /></>)}
        </button>
      </DialogFooter>
    </div>
  );
}

export function LibEspecialForm({ clienteId, clienteNome, onSuccess }) {
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      // Liberação por contingência (por cliente_id) — também DESBLOQUEIA o cadastro se estava travado.
      const { data } = await frentistaApi.post("/otp/liberacao-especial-id", { cliente_id: clienteId, senha });
      onSuccess(data.cliente);
    } catch (err) { modal.error(apiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-3.5">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl flex items-center gap-2">
          <AlertTriangle className="text-rose-500" size={17} /> Liberação Especial
        </DialogTitle>
      </DialogHeader>
      <p className="text-xs text-neutral-600">Este uso será registrado para auditoria. Cliente: <b>{clienteNome}</b></p>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Senha de Contingência</label>
        <input data-testid="lib-senha-input" type="password" value={senha} onChange={(e) => setSenha(e.target.value)}
               className="mt-1 w-full h-12 rounded-xl bg-white border border-neutral-200 px-3.5 text-base outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15 transition"
               autoFocus />
      </div>
      <button data-testid="lib-submit" onClick={submit} disabled={loading}
              className="w-full h-12 btn-accent text-sm font-medium flex items-center justify-center gap-2">
        {loading ? "Verificando..." : (<>Liberar Acesso <ArrowRight size={14} /></>)}
      </button>
    </div>
  );
}

export function IndicacaoForm({ onClose }) {
  const [telefone, setTelefone] = useState("");
  const [loading, setLoading] = useState(false);
  const [codigos, setCodigos] = useState(null);
  const [clienteName, setClienteName] = useState("");

  const submit = async () => {
    const tel = onlyDigits(telefone);
    if (tel.length < 10) return modal.error("Telefone inválido");
    setLoading(true);
    try {
      const { data: cli } = await frentistaApi.get(`/clientes/by-telefone/${tel}`);
      const { data: gen } = await frentistaApi.post(`/indicacao/gerar/${cli.id}`, {});
      setCodigos(gen.codigos);
      setClienteName(cli.nome);
      if (gen.sms_enviado) modal.success("Códigos enviados por SMS");
      else modal.error("Códigos gerados, mas o SMS não pôde ser enviado (verifique saldo/config de SMS). Copie e envie manualmente.");
    } catch (err) {
      if (err.response?.status === 404) modal.error("Cliente não encontrado. Faça o cadastro primeiro.");
      else modal.error(apiError(err));
    } finally { setLoading(false); }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    modal.success(`Código ${code} copiado`);
  };

  if (codigos) {
    return (
      <div className="space-y-3" data-testid="indicacao-success">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Códigos gerados</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-neutral-600">
          3 códigos enviados por SMS para <b>{clienteName}</b>. Válidos para cadastros novos apenas.
        </p>
        <div className="space-y-2">
          {codigos.map((code) => (
            <button key={code} onClick={() => copy(code)}
                    className="w-full flex items-center justify-between rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3 hover:bg-yellow-100 transition">
              <span className="font-mono text-base font-semibold text-amber-900">{code}</span>
              <Copy size={14} className="text-amber-700" />
            </button>
          ))}
        </div>
        <button onClick={onClose} className="w-full h-12 btn-primary-blue text-sm font-medium">
          Fechar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3.5" data-testid="indicacao-form">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
          <Users className="text-amber-600" size={18} /> Campanha de Indicação
        </DialogTitle>
      </DialogHeader>
      <p className="text-xs text-neutral-600">
        Informe o telefone do cliente (já cadastrado) que fará a indicação. O sistema gera <b>3 códigos únicos</b> e envia por SMS para ele compartilhar com amigos. Códigos só funcionam em <b>cadastros novos</b>.
      </p>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Telefone do cliente que indica</label>
        <input
          data-testid="indicacao-telefone"
          value={telefone} onChange={(e) => setTelefone(maskTel(e.target.value))}
          inputMode="numeric" autoFocus
          placeholder="(XX) XXXXX-XXXX"
          className="mt-1.5 w-full h-12 rounded-xl bg-white border border-neutral-200 px-4 text-base outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition"
        />
      </div>
      <button data-testid="indicacao-submit" onClick={submit} disabled={loading}
              className="w-full h-12 btn-primary-blue text-sm font-medium flex items-center justify-center gap-2">
        {loading ? "Gerando..." : (<>Gerar e Enviar Códigos <ArrowRight size={14} /></>)}
      </button>
    </div>
  );
}
